import React from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { device } from '../utils/breakpoints';

const FeaturesWrapper = styled.section`
  background: #F9FAFB;
  padding: 100px 0;
  
  @media ${device.tablet} {
    padding: 60px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 16px;
  
  @media ${device.tablet} {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #000000;
  max-width: 700px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  height: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid #E5E7EB;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  }
  
  @media ${device.tablet} {
    padding: 40px;
  }
`;

const FeatureCol = styled(Col)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    margin-bottom: 32px;
  }
`;

const FeatureHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #EBF5FF 0%, #E0F2FE 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 32px;
`;

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 600;
  color: #000000;
  margin: 0;
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #000000;
  margin: 0;
`;

const FeaturesSection = () => {
  const features = [
    {
      icon: '🛡️',
      title: 'Standardized Deployment Patterns',
      description: 'Standardize how capabilities are deployed so teams aren\'t reinventing patterns. Reduce manual overhead and operational drift.'
    },
    {
      icon: '🔒',
      title: 'Built-in Governance from the Start',
      description: 'Govern access, usage, and operations from day one, rather than bolting controls on later. Meet audit requirements consistently.'
    },
    {
      icon: '🏢',
      title: 'Private Environments, Complete Control',
      description: 'Deploy entirely within private environments-your cloud or on-premises. Sensitive data never leaves your infrastructure.'
    },
    {
      icon: '📊',
      title: 'Visibility & Audit Readiness',
      description: 'Monitor operations with comprehensive metrics, logs, and traces. Maintain audit readiness without manual effort.'
    },
    {
      icon: '⚡',
      title: 'Operational Consistency',
      description: 'Enable teams to innovate safely and consistently within governed boundaries. Less operational drift, fewer surprises over time.'
    },
    {
      icon: '🛠️',
      title: 'Reduce Manual Overhead',
      description: 'Automate operational patterns and reduce long-term risk. Focus on innovation instead of managing bespoke deployments.'
    }
  ];

  return (
    <FeaturesWrapper>
      <Container>
        <SectionHeader>
          <SectionTitle>Governed Operations, Reduced Risk</SectionTitle>
          <SectionSubtitle>
            Standardize how open-source data and AI capabilities are deployed, governed, and operated. 
            Built-in controls from the start, not added later.
          </SectionSubtitle>
        </SectionHeader>
        
        <Row>
          {features.map((feature, index) => (
            <FeatureCol lg={4} md={6} key={index}>
              <FeatureCard>
                <FeatureHeader>
                  <IconWrapper>{feature.icon}</IconWrapper>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                </FeatureHeader>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            </FeatureCol>
          ))}
        </Row>
      </Container>
    </FeaturesWrapper>
  );
};

export default FeaturesSection;


